import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IBillingInfo } from '@global/types/types';
import { useModal } from '@global/hooks/useModal';

import { AddressDoctor } from '@global/components/AddressDoctor';
import { CountrySelectInput } from '@global/components/ActiveForm/Elements/SelectCountry';
import { ActiveInputText } from '@global/components/ActiveForm/Elements/ActiveInputText';
import { StateSelectInput } from '@global/components/ActiveForm/Elements/SelectState';
import { ActiveInputFormatted } from '@global/components/ActiveForm/Elements/ActiveInputFormatted';
import { Checkbox } from '@global/components/ActiveForm/Elements/Checkbox';
import { ButtonCTA } from '@global/components/UI Elements/ButtonCTA';
import { BannerMessage } from '@global/components/BannerMessage';
import { getSuggestAddresses } from '@global/components/BillingInfoForm/getSuggestAddress';
import { checkForUnavailableProducts } from '@global/components/BillingInfoForm/checkForUnavailableProducts';
import { saveCartBillingAddress } from '@global/components/BillingInfoForm/saveCartBillingAddress';
import { getLocaleConfig } from '@global/localization/services';

import {
  BillingFormType,
  BillingInfoFormProps,
  BillingInfoFormValuesType,
} from '@global/components/BillingInfoForm/types';
import { useWpsPaymentForm } from '../WpsPaymentForm/hooks';
import { cn } from '@global/utils/styling';
import {
  getStateFromDropdown,
  setZipCodeFormData,
} from '@src/pages/checkout/components/BillingInformation/helper';

export const BillingInfoForm: FC<BillingInfoFormProps> = ({
  toggleAgreementCheck,
  onSubmit,
  agreementText,
  agreementCheck,
  isCartButtonLoading,
  billingInfo,
  isLoading,
  buttonId,
  setIsCartButtonLoading,
  buttonText,
  buttonCancelText,
  buttonCancelAction,
  enabledAddressDoctor,
  formType,
  withPaymentForm,
  onCountryRestrictedProducts,
  hiddenForm,
  setPaymentErr,
  isCountrySelectDisabled,
  disableCountryLocaleFallback,
  region,
  restrictedCountries,
  isPhysical,
}) => {
  const BILLING_ADDRESS_FIELDS = [
    'country',
    'address1',
    'address2',
    'zip',
    'city',
    'state',
  ];

  const methods = useFormContext<BillingInfoFormValuesType>();
  const [q0ValidationErrorMessage, setQ0ValidationErrorMessage] = useState<
    string | null
  >(null);
  const [q0RetryCounter, setQ0RetryCounter] = useState(0);
  const [isQ0RetryInProgress, setIsQ0RetryInProgress] = useState(false);

  const { t } = useTranslation();
  const { scrollToPaymentForm, submitPaymentForm, isPaymentFormFilled } =
    useWpsPaymentForm();
  const { name, addressOne, addressTwo, zip, city, state, tel, country } =
    billingInfo || {};
  const hasPersistedBillingDetails = Boolean(
    name || addressOne || city || zip || tel
  );

  const { handleOpen: handleOpenAddressDoctorModal } = useModal<IBillingInfo[]>(
    {
      customClass: 'address-doctor',
      customOverlayClass: 'address-doctor-overlay',
      content: ({ handleClose, customData }) => (
        <AddressDoctor
          handleClose={() => {
            handleClose();
          }}
          handleSubmit={async (billingInfo: IBillingInfo) => {
            const countryCode = methods.getValues('country');
            const { stateDropdown } = getLocaleConfig(countryCode);
            const state = stateDropdown ? billingInfo.state : '';

            methods.setValue('address1', billingInfo.addressOne);
            methods.setValue('address2', billingInfo.addressTwo);

            methods.setValue('city', billingInfo.city);
            setZipCodeFormData(billingInfo.zip, countryCode, methods);
            methods.setValue('state', state);
            submitForm();
          }}
          customData={customData}
        />
      ),
    }
  );

  const isLoadingSubmitBtn = isLoading || isCartButtonLoading;

  useEffect(() => {
    scrollToPaymentForm();
  }, []);

  useEffect(() => {
    if (!q0ValidationErrorMessage) {
      return;
    }

    const subscription = methods.watch((_value, { name, type }) => {
      if (type !== 'change' || !name) {
        return;
      }

      if (BILLING_ADDRESS_FIELDS.includes(name)) {
        setQ0RetryCounter((prev) => prev + 1);
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, q0ValidationErrorMessage]);

  useEffect(() => {
    if (!q0ValidationErrorMessage || !q0RetryCounter) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      const currentBillingAddress = methods.getValues();
      const updatedBillingAddress = {
        ...currentBillingAddress,
        state: getStateFromDropdown(
          currentBillingAddress?.country,
          currentBillingAddress.state
        ),
      };

      setIsQ0RetryInProgress(true);
      const suggestAddressesResult = await getSuggestAddresses(
        updatedBillingAddress
      );

      if (!suggestAddressesResult.hasQ0Failure) {
        setQ0ValidationErrorMessage(null);
      } else {
        setQ0ValidationErrorMessage(
          suggestAddressesResult.validationMessage || q0ValidationErrorMessage
        );
      }

      setIsQ0RetryInProgress(false);
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [methods, q0RetryCounter, q0ValidationErrorMessage]);

  const submitForm = () => {
    setIsCartButtonLoading(true);

    if (withPaymentForm) {
      setPaymentErr && setPaymentErr(null);
      submitPaymentForm();
    }

    onSubmit && onSubmit();
  };

  const submitWPSForm = methods.handleSubmit(async () => {
    const currentBillingAddress = methods.getValues();
    const updatedBillingAddress = {
      ...currentBillingAddress,
      state: getStateFromDropdown(
        currentBillingAddress?.country,
        currentBillingAddress.state
      ),
    };

    const enabledCheckForRestrictions =
      formType === BillingFormType.BillingForm && !isPhysical;

    if (enabledCheckForRestrictions) {
      const { countryRestrictedProducts } = await checkForUnavailableProducts(
        updatedBillingAddress.country
      );
      if (countryRestrictedProducts.length && onCountryRestrictedProducts) {
        await saveCartBillingAddress(updatedBillingAddress);
        return onCountryRestrictedProducts(countryRestrictedProducts);
      }
    }

    if (
      !isPaymentFormFilled &&
      formType !== BillingFormType.ChangeBillingForm
    ) {
      setPaymentErr &&
        setPaymentErr(t('checkout.step2.validationErr.emptyData'));
      scrollToPaymentForm();
      return;
    }

    if (!enabledAddressDoctor) {
      submitForm();
      return;
    }

    // FORMTYPE USED FOR GETTING WHICH FORM IS RENDERED [CHANGEBILLINGFORM, BILLINGFORM]
    if (formType === BillingFormType.ChangeBillingForm) {
      setIsCartButtonLoading(true);
    }

    const suggestAddressesResult = await getSuggestAddresses(
      updatedBillingAddress
    );

    if (suggestAddressesResult.hasQ0Failure) {
      setQ0ValidationErrorMessage(
        suggestAddressesResult.validationMessage ||
          'Your billing address could not be verified. Please check your details and try again. If the issue persists, please contact support.'
      );
      return;
    }

    setQ0ValidationErrorMessage(null);

    if (suggestAddressesResult.addresses.length > 1) {
      handleOpenAddressDoctorModal(suggestAddressesResult.addresses);
      return;
    }

    submitForm();
  });

  return (
    <form onSubmit={submitWPSForm}>
      <div className="billing-info__form" hidden={hiddenForm}>
        {/* COUNTRY */}
        <CountrySelectInput
          selectedCountry={{
            isoCodeTwo: hasPersistedBillingDetails ? country?.iso2Code : '',
          }}
          region={region}
          restrictedCountries={restrictedCountries}
          enableLocaleFallback={!disableCountryLocaleFallback}
          disabled={isCountrySelectDisabled}
          fieldName="country"
          label={t('activeForm.billingCountryLabel')}
        />
        {/* NAME */}
        <div className="billing-info__input-field-wrapper">
          <ActiveInputText
            inputName="CCname"
            inputId="card_billingaddress_customerName"
            defaultValue={name}
          />
        </div>

        {/* ADDRESS */}
        <div className="billing-info__address-field-wrapper">
          <ActiveInputText
            inputName="address1"
            inputId="card_billingaddress_addressLine1"
            defaultValue={addressOne}
          />
          <ActiveInputText
            className="billing-info__address-field-2"
            inputName="address2"
            inputId="card_billingaddress_addressLine2"
            defaultValue={addressTwo}
          />
        </div>

        {/* ZIP CODE */}
        <div className="billing-info__zipcode-field-wrapper">
          <ActiveInputText
            inputName="zip"
            inputId="card_billingaddress_postCode"
            defaultValue={zip}
          />
        </div>

        {/* CITY */}
        <div className="billing-info__input-field-wrapper">
          <ActiveInputText
            inputName="city"
            inputId="card_billingaddress_city"
            defaultValue={city}
          />
        </div>

        {/* STATE */}
        <StateSelectInput
          parentFieldName="country"
          fieldName="state"
          selectedState={state}
        />

        {/* PHONE NUMBER */}
        <div className="billing-info__input-field-wrapper">
          <ActiveInputFormatted inputName="phone" defaultValue={tel} />
        </div>

        {toggleAgreementCheck && agreementText && (
          <Checkbox
            isHidden={false}
            isChecked={agreementCheck}
            text={agreementText}
            toggle={toggleAgreementCheck}
          />
        )}
      </div>

      <div
        className={cn('billing-info__buttons-wrapper', {
          'zero-padding': hiddenForm,
          'lg-row': formType === BillingFormType.ChangeBillingForm,
        })}
      >
        <BannerMessage
          type="error"
          hidden={!q0ValidationErrorMessage}
          message={q0ValidationErrorMessage || ''}
        />
        {!tel && formType === BillingFormType.ChangePaymentForm ? (
          <span className="common-input__error-message">
            {t('activeForm.validationErr.customPhoneReq')}
          </span>
        ) : (
          ''
        )}
        <ButtonCTA
          tabIndex={-1}
          variant="primary"
          type="submit"
          className="billing-info__submit-btn"
          disabled={Boolean(q0ValidationErrorMessage) || isQ0RetryInProgress}
        >
          {isLoadingSubmitBtn ? <div className="spinner" /> : buttonText}
        </ButtonCTA>
        {buttonCancelText && buttonCancelAction && (
          <ButtonCTA
            variant="secondary"
            className="billing-info__cancel-btn small-btn"
            onClick={buttonCancelAction}
          >
            {buttonCancelText}
          </ButtonCTA>
        )}
        <button hidden id={buttonId}></button>
      </div>
    </form>
  );
};
